import { AppButton } from "@/components/teacher/AppButton";
import { AppDialog } from "@/components/teacher/AppDialog";
import {
  LessonSlideGrid,
  type LessonSlideGridItem,
  LessonSlideGridSkeleton,
} from "@/components/teacher/LessonSlideGrid";
import { LibraryBrowser, type LibraryLesson } from "@/components/teacher/LibraryBrowser";
import { StatPill } from "@/components/teacher/StatPill";
import { GetProjectGroupsDocument, TagsDocument } from "@/generated/graphql";
import { format, useT } from "@/i18n";
import {
  type SlideWithSubslides,
  useSlidesByLessonId,
} from "@/lib/hooks/slides/useSlidesByLessonId";
import type { AssignableReach } from "@/lib/utils/assignableReach";
import type { SlideFields } from "@/types/Slide";
import { useQuery } from "@apollo/client/react";
import { useState } from "react";
import { HiChevronLeft } from "react-icons/hi";

const ALL = "__all__";
const SKELETON_COUNT = 10;
const INPUT_CLASS =
  "h-9 rounded-full border border-gray-200 bg-white px-3 body-sm text-app-text placeholder:text-app-dim focus:outline-none focus:ring-2 focus:ring-app-aqua";
/** A filter shares a phone row with one other, and sizes to its own content from `sm`. */
const FILTER_CLASS = `${INPUT_CLASS} min-w-0 flex-1 basis-40 sm:flex-none sm:basis-auto`;

/**
 * One picked slide, and every slide id it carries into the homework — a flat list, in play order.
 * A slide carried over from an existing assignment has no lesson to name.
 */
export interface SelectedSlide {
  slide: SlideFields;
  slideIds: string[];
  lessonName?: string;
  lessonPath?: string;
}

interface SlidePickerDialogProps {
  /** Seeds the dialog; Cancel leaves it untouched, Done hands back what the teacher built. */
  initialSelection: Map<string, SelectedSlide>;
  /** Marks the projects this assignment cannot use, so a teacher sees why before picking from one. */
  reach?: AssignableReach | undefined;
  reachLoading?: boolean | undefined;
  onCancel: () => void;
  onDone: (selection: Map<string, SelectedSlide>) => void;
}

/** A main slide and its subslides, as homework stores them: the group is one pick. */
function toSelected(
  slide: SlideWithSubslides,
  lessonName: string,
  lessonPath: string
): SelectedSlide {
  return {
    slide,
    slideIds: [slide.id, ...slide.children.map((child) => child.id)],
    lessonName,
    lessonPath,
  };
}

/**
 * Full screen on purpose: picking slides means recognising them, and a name and a template label
 * never told a teacher what a slide actually looks like.
 */
export function SlidePickerDialog({
  initialSelection,
  reach,
  reachLoading,
  onCancel,
  onDone,
}: SlidePickerDialogProps) {
  const t = useT();
  const [selection, setSelection] = useState(() => new Map(initialSelection));
  const [lesson, setLesson] = useState<LibraryLesson | null>(null);
  const [groupFilter, setGroupFilter] = useState(ALL);
  const [tagFilter, setTagFilter] = useState(ALL);
  const [templateFilter, setTemplateFilter] = useState(ALL);
  const [search, setSearch] = useState("");

  const tagsQuery = useQuery(TagsDocument, { variables: { isArchived: false } });
  // Groups belong to the lesson's own project. Shown whatever `useGroup` says: this is an
  // authoring filter, not the student-facing layout.
  const groupsQuery = useQuery(GetProjectGroupsDocument, {
    variables: { id: lesson?.parentId ?? "" },
    skip: !lesson?.parentId,
  });
  const { lessonSlideItems, lessonSlidesLoading, lessonSlidesError } = useSlidesByLessonId({
    lessonId: lesson?.id ?? "",
    skip: !lesson,
  });

  const tagNames = new Map((tagsQuery.data?.tags ?? []).map((tag) => [tag.id, tag.name]));
  const lessonTagIds = [...new Set(lessonSlideItems.flatMap((slide) => slide.tagIds))];
  const lessonTemplates = [...new Set(lessonSlideItems.map((slide) => slide.template))];

  const lessonGroups = [...(groupsQuery.data?.project?.groups ?? [])]
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .filter((group) => group.tagIds.some((tagId) => lessonTagIds.includes(tagId)));
  const groupTagIds = lessonGroups.find((group) => group.id === groupFilter)?.tagIds;

  const visible = lessonSlideItems.filter((slide) => {
    if (groupTagIds && !slide.tagIds.some((tagId) => groupTagIds.includes(tagId))) return false;
    if (tagFilter !== ALL && !slide.tagIds.includes(tagFilter)) return false;
    if (templateFilter !== ALL && slide.template !== templateFilter) return false;
    return slide.name.toLowerCase().includes(search.trim().toLowerCase());
  });

  const items: LessonSlideGridItem[] = visible.map((slide) => ({
    slide,
    children: slide.children,
    // Its place in the lesson, not in the filtered list the teacher happens to be looking at.
    number: lessonSlideItems.indexOf(slide) + 1,
  }));

  const toggle = (slideId: string) => {
    const slide = lessonSlideItems.find((candidate) => candidate.id === slideId);
    if (!(slide && lesson)) return;

    const next = new Map(selection);
    if (!next.delete(slideId)) next.set(slideId, toSelected(slide, lesson.name, lesson.path));
    setSelection(next);
  };

  const setVisibleSelected = (selected: boolean) => {
    if (!lesson) return;

    const next = new Map(selection);
    for (const slide of visible) {
      if (selected) {
        next.set(slide.id, toSelected(slide, lesson.name, lesson.path));
      } else {
        next.delete(slide.id);
      }
    }
    setSelection(next);
  };

  const slideCount = [...selection.values()].reduce((sum, item) => sum + item.slideIds.length, 0);

  return (
    <AppDialog
      label={t["assign.chooseSlides"]}
      size={lesson ? "wlg" : "lg"}
      bodyMeasure={lesson ? "w-full max-w-none" : undefined}
      onDismiss={onCancel}
      title={
        <>
          {lesson ? (
            <AppButton tone="quiet" size="sm" onClick={() => setLesson(null)}>
              <HiChevronLeft className="h-4 w-4" aria-hidden="true" />
              {t["library.root"]}
            </AppButton>
          ) : null}
          <span className="app-title min-w-0 flex-1 truncate">
            {lesson?.name ?? t["assign.chooseSlides"]}
          </span>
          <StatPill tone={slideCount > 0 ? "aqua" : "muted"}>
            {format(t["assign.slideCount"], { count: slideCount })}
          </StatPill>
        </>
      }
      actions={
        <>
          <AppButton tone="quiet" size="sm" onClick={onCancel}>
            {t["common.cancel"]}
          </AppButton>
          <AppButton size="sm" onClick={() => onDone(selection)}>
            {t["common.done"]}
          </AppButton>
        </>
      }
      toolbar={
        lesson ? (
          <div className="flex flex-wrap items-center gap-2">
            {lessonGroups.length > 0 ? (
              <select
                aria-label={t["picker.filterByGroup"]}
                value={groupFilter}
                onChange={(event) => setGroupFilter(event.target.value)}
                className={FILTER_CLASS}
              >
                <option value={ALL}>{t["picker.allGroups"]}</option>
                {lessonGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
            ) : null}
            <select
              aria-label={t["picker.filterByTag"]}
              value={tagFilter}
              onChange={(event) => setTagFilter(event.target.value)}
              className={FILTER_CLASS}
            >
              <option value={ALL}>{t["picker.allTags"]}</option>
              {lessonTagIds.map((tagId) => (
                <option key={tagId} value={tagId}>
                  {tagNames.get(tagId) ?? tagId}
                </option>
              ))}
            </select>
            <select
              aria-label={t["picker.filterByTemplate"]}
              value={templateFilter}
              onChange={(event) => setTemplateFilter(event.target.value)}
              className={FILTER_CLASS}
            >
              <option value={ALL}>{t["picker.allTemplates"]}</option>
              {lessonTemplates.map((template) => (
                <option key={template} value={template}>
                  {template}
                </option>
              ))}
            </select>
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t["picker.search"]}
              className={`${INPUT_CLASS} min-w-40 flex-1`}
            />
            <div>
              <AppButton
                tone="quiet"
                size="sm"
                className="flex-1 sm:flex-none"
                onClick={() => setVisibleSelected(true)}
              >
                {format(t["picker.selectVisible"], { count: visible.length })}
              </AppButton>
              <AppButton
                tone="quiet"
                size="sm"
                className="flex-1 sm:flex-none"
                onClick={() => setVisibleSelected(false)}
              >
                {t["picker.deselectVisible"]}
              </AppButton>
            </div>
          </div>
        ) : undefined
      }
    >
      {!lesson ? (
        <div className="app-card p-4 sm:p-5">
          <LibraryBrowser onOpenLesson={setLesson} reach={reach} reachLoading={reachLoading} />
        </div>
      ) : lessonSlidesError && lessonSlideItems.length === 0 ? (
        <p className="app-card body-sm p-6 text-app-overdue">{t["lesson.slidesLoadError"]}</p>
      ) : lessonSlidesLoading && lessonSlideItems.length === 0 ? (
        <LessonSlideGridSkeleton count={SKELETON_COUNT} />
      ) : items.length === 0 ? (
        <p className="app-card body-sm p-6 text-app-muted">{t["picker.noMatches"]}</p>
      ) : (
        <LessonSlideGrid
          items={items}
          onPickSlide={toggle}
          selectedIds={new Set(selection.keys())}
        />
      )}
    </AppDialog>
  );
}
